import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
    MdVerifiedUser,
    MdGroup,
    MdSwapHoriz,
    MdCallMerge,
    MdHistory,
    MdArrowForward,
    MdCheckCircle,
    MdCancel,
    MdInfoOutline,
    MdWarningAmber,
    MdPersonAddAlt,
} from 'react-icons/md';
import {
    useClaimClientMutation,
    useApproveClaimMutation,
    useRejectClaimMutation,
    useMergeClientsMutation,
    useReassignClientMutation,
    useGetAuditTrailQuery,
    useGetClaimStatusQuery,
} from '@redux/modules/clientOwnership';
import clients from '@redux/modules/clients';
import tmrAccounts from '@redux/modules/tmrAccounts';
import { apiUrls } from '@constants/apiurls';
import styles from './clientOwnership.module.scss';

const ACTION_LABELS = {
    claim_requested: 'Claim Requested',
    claim_approved: 'Claim Approved',
    claim_rejected: 'Claim Rejected',
    merged: 'Merged',
    ownership_reassigned: 'Reassigned',
};

const ACTION_STYLES = {
    claim_requested: styles.claimRequested,
    claim_approved: styles.claimApproved,
    claim_rejected: styles.claimRejected,
    merged: styles.merged,
    ownership_reassigned: styles.reassigned,
};

const initials = (title = '') =>
    title
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || 'C';

export const ClientOwnershipPanel = ({ clientId, clientData, onOwnershipChange }) => {
    const [tab, setTab] = useState('overview');
    const [mergeTargetId, setMergeTargetId] = useState('');
    const [reassignAccountId, setReassignAccountId] = useState('');
    const [reassignEmail, setReassignEmail] = useState('');

    const { data: claimStatus, refetch: refetchClaimStatus } = useGetClaimStatusQuery(clientId, { skip: !clientId });
    const { data: auditData } = useGetAuditTrailQuery(clientId, {
        skip: !clientId || tab !== 'audit',
    });
    const [claimClient, { isLoading: claiming }] = useClaimClientMutation();
    const [approveClaim, { isLoading: approving }] = useApproveClaimMutation();
    const [rejectClaim, { isLoading: rejecting }] = useRejectClaimMutation();
    const [mergeClients, { isLoading: merging }] = useMergeClientsMutation();
    const [reassignClient, { isLoading: reassigning }] = useReassignClientMutation();

    const { data: targetClientData } = clients.list().useClientsdetailQuery(
        { params: { id: mergeTargetId }, extendedPath: apiUrls.getClientDetail() },
        { skip: !mergeTargetId },
    );

    // Fetch target TMR account user info for reassign preview
    const { data: reassignUserData } =
        tmrAccounts.form().useTmrAccountuserInfoQuery(reassignAccountId, {
            skip: !reassignAccountId,
        });

    // Current user's TMR account (to know if THEY own this client)
    const { data: myAccountData } = tmrAccounts
        .list()
        .useTmrAccountdetailQuery({ extendedPath: '/current' });
    const myAccountId = myAccountData?.data?.account?.id;

    const toStr = (v) => (Array.isArray(v) ? v.join(', ') : String(v || ''));

    const handleError = (e, fallback) => {
        const err = e?.data?.errors || e?.data?.message || e?.error || fallback;
        toast.error(toStr(err));
    };

    const handleSuccess = (r, fallback) => {
        toast.success(toStr(r?.messages || fallback));
        if (refetchClaimStatus) refetchClaimStatus();
        if (onOwnershipChange) onOwnershipChange();
    };

    const ownerAccountId = clientData?.owner_account_id;
    const ownedByMe = ownerAccountId && myAccountId && ownerAccountId === myAccountId;

    // Only owners can manage (merge/reassign)
    const TABS = [
        { key: 'overview', label: 'Overview', icon: <MdVerifiedUser /> },
        ...(ownedByMe
            ? [
                { key: 'merge', label: 'Merge', icon: <MdCallMerge /> },
                { key: 'reassign', label: 'Reassign', icon: <MdSwapHoriz /> },
            ]
            : []),
        { key: 'audit', label: 'History', icon: <MdHistory /> },
    ];

    return (
        <div className={styles.panel}>
            {/* Header */}
            <div className={styles.panelHeader}>
                <div className={styles.headerLeft}>
                    <div className={styles.avatar}>{initials(clientData?.title)}</div>
                    <div>
                        <div className={styles.headerTitle}>
                            {clientData?.title || `Client #${clientId}`}
                        </div>
                        <div className={styles.headerSub}>Client ownership &amp; transfers</div>
                    </div>
                </div>
                {ownerAccountId ? (
                    <span className={`${styles.badge} ${styles.badgeOwned}`}>
                        <MdVerifiedUser />{' '}
                        {ownedByMe ? 'Owned by you' : `Account #${ownerAccountId}`}
                    </span>
                ) : (
                    <span className={`${styles.badge} ${styles.badgeUnowned}`}>
                        <MdWarningAmber /> Unowned
                    </span>
                )}
            </div>

            {/* Tabs */}
            <div className={styles.tabBar}>
                {TABS.map((t) => (
                    <button
                        type="button"
                        key={t.key}
                        className={`${styles.tabBtn} ${tab === t.key ? styles.tabActive : ''}`}
                        onClick={() => setTab(t.key)}
                    >
                        <span className={styles.tabIcon}>{t.icon}</span>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className={styles.tabContent}>
                {tab === 'overview' && (
                    <ClaimSection
                        clientData={clientData}
                        claimStatus={claimStatus?.data}
                        ownedByMe={ownedByMe}
                        onClaim={() =>
                            claimClient(clientId)
                                .unwrap()
                                .then((r) => handleSuccess(r, 'Claim submitted'))
                                .catch((e) => handleError(e, 'Claim failed'))
                        }
                        onApprove={(accountId) =>
                            approveClaim({ clientId, owner_account_id: accountId })
                                .unwrap()
                                .then((r) => handleSuccess(r, 'Claim approved'))
                                .catch((e) => handleError(e, 'Approval failed'))
                        }
                        onReject={() =>
                            rejectClaim(clientId)
                                .unwrap()
                                .then((r) => handleSuccess(r, 'Claim rejected'))
                                .catch((e) => handleError(e, 'Rejection failed'))
                        }
                        loading={{ claiming, approving, rejecting }}
                    />
                )}

                {tab === 'merge' && (
                    <MergeSection
                        clientId={clientId}
                        clientData={clientData}
                        mergeTargetId={mergeTargetId}
                        setMergeTargetId={setMergeTargetId}
                        targetClient={targetClientData?.data}
                        merging={merging}
                        onMerge={() =>
                            mergeClients({ sourceId: clientId, targetId: mergeTargetId })
                                .unwrap()
                                .then((r) => handleSuccess(r, 'Merge complete'))
                                .catch((e) => handleError(e, 'Merge failed'))
                        }
                    />
                )}

                {tab === 'reassign' && (
                    <ReassignSection
                        clientData={clientData}
                        reassignAccountId={reassignAccountId}
                        setReassignAccountId={setReassignAccountId}
                        reassignEmail={reassignEmail}
                        setReassignEmail={setReassignEmail}
                        reassigning={reassigning}
                        reassignUserData={reassignUserData?.data}
                        onReassign={() =>
                            reassignClient({ clientId, owner_account_id: reassignAccountId })
                                .unwrap()
                                .then((r) => handleSuccess(r, 'Reassigned'))
                                .catch((e) => handleError(e, 'Reassignment failed'))
                        }
                    />
                )}

                {tab === 'audit' && <AuditSection auditData={auditData?.data} />}
            </div>
        </div>
    );
};

const ClaimSection = ({ clientData, claimStatus, ownedByMe, onClaim, onApprove, onReject, loading }) => {
    const hasPending = claimStatus?.has_pending_claim;
    const isOwned = !!clientData?.owner_account_id;

    // Pending claim — the owner decides; for clients without an active owner,
    // a Holedo admin does (can_review).
    if (hasPending && (ownedByMe || claimStatus?.can_review)) {
        return (
            <div className={`${styles.stateCard} ${styles.stateInfo}`}>
                <div className={styles.stateIcon}>
                    <MdGroup />
                </div>
                <div className={styles.stateBody}>
                    <h4>Pending claim request</h4>
                    <p>
                        TMR Account{' '}
                        <strong>#{claimStatus?.pending_claim?.new_owner_account_id}</strong> has
                        requested ownership of this client.
                    </p>
                    <div className={styles.actionRow}>
                        <button
                            type="button"
                            className={styles.approveBtn}
                            disabled={loading.approving}
                            onClick={() => onApprove(claimStatus?.pending_claim?.new_owner_account_id)}
                        >
                            <MdCheckCircle />
                            {loading.approving ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                            type="button"
                            className={styles.rejectBtn}
                            disabled={loading.rejecting}
                            onClick={onReject}
                        >
                            <MdCancel />
                            {loading.rejecting ? 'Rejecting…' : 'Reject'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Current user owns this client
    if (ownedByMe) {
        return (
            <div className={`${styles.stateCard} ${styles.stateNeutral}`}>
                <div className={styles.stateIcon}>
                    <MdInfoOutline />
                </div>
                <div className={styles.stateBody}>
                    <h4>You own this client</h4>
                    <p>
                        Use the Merge, Reassign, or History tabs to manage{' '}
                        <strong>{clientData?.title}</strong>.
                    </p>
                </div>
            </div>
        );
    }

    // A claim is already pending (by this or another user)
    if (hasPending) {
        return (
            <div className={`${styles.stateCard} ${styles.stateInfo}`}>
                <div className={styles.stateIcon}>
                    <MdGroup />
                </div>
                <div className={styles.stateBody}>
                    <h4>Claim pending</h4>
                    <p>
                        A claim request for <strong>{clientData?.title}</strong> is awaiting{' '}
                        {isOwned ? 'the current owner\u2019s approval' : 'review by Holedo'}.
                    </p>
                </div>
            </div>
        );
    }

    // Unowned client — direct claim
    if (!isOwned) {
        return (
            <div className={`${styles.stateCard} ${styles.stateWarn}`}>
                <div className={styles.stateIcon}>
                    <MdPersonAddAlt />
                </div>
                <div className={styles.stateBody}>
                    <h4>This client has no owner</h4>
                    <p>
                        <strong>{clientData?.title}</strong> is unclaimed. As a Business or
                        Enterprise account holder, you can request it to manage its jobs, notes,
                        and settings. Holedo reviews each request before handing a client over.
                    </p>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        disabled={loading.claiming}
                        onClick={onClaim}
                    >
                        <MdVerifiedUser />
                        {loading.claiming ? 'Requesting…' : 'Request This Client'}
                    </button>
                </div>
            </div>
        );
    }

    // Owned by another account — request a claim
    return (
        <div className={`${styles.stateCard} ${styles.stateWarn}`}>
            <div className={styles.stateIcon}>
                <MdPersonAddAlt />
            </div>
            <div className={styles.stateBody}>
                <h4>Owned by another account</h4>
                <p>
                    <strong>{clientData?.title}</strong> is currently owned by TMR Account{' '}
                    <strong>#{clientData.owner_account_id}</strong>. Send a claim request — the
                    current owner will be notified to approve or reject it.
                </p>
                <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={loading.claiming}
                    onClick={onClaim}
                >
                    <MdVerifiedUser />
                    {loading.claiming ? 'Requesting…' : 'Request Claim'}
                </button>
            </div>
        </div>
    );
};

const MergeSection = ({ clientId, clientData, mergeTargetId, setMergeTargetId, targetClient, merging, onMerge }) => (
    <div className={styles.section}>
        <div className={styles.sectionHead}>
            <MdCallMerge />
            <div>
                <h4>Merge into another client</h4>
                <p>Consolidate duplicate clients into a single record.</p>
            </div>
        </div>

        <div className={styles.mergeSummary}>
            <div className={`${styles.mergeCard} ${styles.mergeSource}`}>
                <div className={styles.mergeLabel}>Source · archived</div>
                <div className={styles.mergeName}>{clientData?.title}</div>
                <div className={styles.mergeId}>ID #{clientId}</div>
            </div>
            <div className={styles.mergeArrow}>
                <MdArrowForward />
            </div>
            <div className={`${styles.mergeCard} ${styles.mergeTarget}`}>
                <div className={styles.mergeLabel}>Target · survives</div>
                {targetClient ? (
                    <>
                        <div className={styles.mergeName}>{targetClient.title}</div>
                        <div className={styles.mergeId}>ID #{targetClient.id}</div>
                    </>
                ) : (
                    <div className={styles.mergePlaceholder}>Enter an ID below</div>
                )}
            </div>
        </div>

        <div className={styles.formGroup}>
            <label>Target Client ID</label>
            <input
                type="number"
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                placeholder="e.g. 91"
            />
        </div>

        <div className={styles.warningBanner}>
            <MdWarningAmber />
            <span>
                All jobs, notes, and uploads from <strong>{clientData?.title}</strong> will
                be moved to the target. This client will be archived.
            </span>
        </div>

        <button type="button" className={styles.primaryBtn} disabled={merging || !mergeTargetId} onClick={onMerge}>
            <MdCallMerge />
            {merging ? 'Merging…' : 'Merge Clients'}
        </button>
    </div>
);

const ReassignSection = ({ clientData, reassignAccountId, setReassignAccountId, reassignEmail, setReassignEmail, reassigning, reassignUserData, onReassign }) => (
    <div className={styles.section}>
        <div className={styles.sectionHead}>
            <MdSwapHoriz />
            <div>
                <h4>Reassign ownership</h4>
                <p>
                    Transfer <strong>{clientData?.title}</strong> from Account{' '}
                    <strong>#{clientData?.owner_account_id}</strong> to another TMR account.
                </p>
            </div>
        </div>

        <div className={styles.formRow}>
            <div className={styles.formGroup}>
                <label>New Owner TMR Account ID</label>
                <input
                    type="number"
                    value={reassignAccountId}
                    onChange={(e) => setReassignAccountId(e.target.value)}
                    placeholder="e.g. 21840"
                />
            </div>
            <div className={styles.formGroup}>
                <label>User Email (reference)</label>
                <input
                    type="email"
                    value={reassignEmail}
                    onChange={(e) => setReassignEmail(e.target.value)}
                    placeholder="user@example.com"
                />
            </div>
        </div>

        {reassignUserData?.user && (
            <div className={styles.previewCard}>
                <div className={styles.previewLabel}>Target account owner</div>
                <div className={styles.previewRow}>
                    <span className={styles.previewKey}>Name</span>
                    <span className={styles.previewVal}>
                        {reassignUserData.user.first_name} {reassignUserData.user.last_name}
                    </span>
                </div>
                <div className={styles.previewRow}>
                    <span className={styles.previewKey}>Slug</span>
                    <span className={styles.previewVal}>{reassignUserData.user.slug}</span>
                </div>
                <div className={styles.previewRow}>
                    <span className={styles.previewKey}>Account</span>
                    <span className={styles.previewVal}>
                        #{reassignUserData.id} &middot; {reassignUserData.account_level} &middot;{' '}
                        {reassignUserData.status}
                    </span>
                </div>
            </div>
        )}

        <div className={styles.warningBanner}>
            <MdInfoOutline />
            <span>The target account must be active and have available client capacity.</span>
        </div>

        <button
            type="button"
            className={styles.primaryBtn}
            disabled={reassigning || !reassignAccountId}
            onClick={onReassign}
        >
            <MdSwapHoriz />
            {reassigning ? 'Reassigning…' : 'Reassign Ownership'}
        </button>
    </div>
);

const AuditSection = ({ auditData }) => {
    if (!auditData?.length) {
        return (
            <div className={styles.emptyState}>
                <MdHistory />
                <p>No ownership events recorded for this client yet.</p>
            </div>
        );
    }

    return (
        <div className={styles.timeline}>
            {auditData.map((entry) => (
                <div key={entry.id} className={styles.timelineItem}>
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineContent}>
                        <div className={styles.timelineTop}>
                            <span className={`${styles.actionTag} ${ACTION_STYLES[entry.action] || ''}`}>
                                {ACTION_LABELS[entry.action] || entry.action}
                            </span>
                            <span className={styles.timelineDate}>
                                {new Date(entry.created).toLocaleString()}
                            </span>
                        </div>
                        <div className={styles.timelineMeta}>
                            <span>By User #{entry.actor_user_id || '—'}</span>
                            {entry.previous_owner_account_id && (
                                <span>
                                    From Account #{entry.previous_owner_account_id}
                                    {entry.new_owner_account_id && ` → #${entry.new_owner_account_id}`}
                                </span>
                            )}
                            {!entry.previous_owner_account_id && entry.new_owner_account_id && (
                                <span>To Account #{entry.new_owner_account_id}</span>
                            )}
                            {entry.merged_into_client_id && (
                                <span>Merged into Client #{entry.merged_into_client_id}</span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};
